import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './EducationPage.module.css';

// Import logos directly
import aaubLogo from '../assets/logo/aaub_logo.png';
import sgcLogo from '../assets/logo/sgc_logo.jpg';
import sgphsLogo from '../assets/logo/sgphs_logo.jpg';

const logoMap = {
  "Aviation and Aerospace University, Bangladesh (AAUB)": aaubLogo,
  "Sylhet Government College, Sylhet": sgcLogo,
  "Sylhet Government Pilot High School": sgphsLogo
};

export default function EducationPage({ data }) {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [expandedThesis, setExpandedThesis] = useState(() => {
    const eduItems = data.education?.items || data.education || [];
    const firstThesisIndex = eduItems.findIndex((edu) => Boolean(edu.thesis));
    return firstThesisIndex >= 1 ? firstThesisIndex : null;
  });

  const eduItems = data.education?.items || data.education || [];
  const memories = data.education?.memories || data.memories || [];

  // HARDCODED RESEARCH-READY COURSEWORK & PREPARATION
  const researchPrep = {
    coreCourses: [
      "Aerodynamics & Flight Mechanics",
      "Aircraft Structures & Structural Analysis",
      "Propulsion Systems & Turbomachinery",
      "Aircraft Design & Systems Engineering",
      "Avionics & Control Systems",
      "Computational Fluid Dynamics (CFD)",
      "Finite Element Analysis (FEA)",
      "Materials Science & Engineering"
    ],
    researchMethods: [
      "Machine Learning & Deep Learning (TensorFlow, PyTorch, Keras)",
      "Experimental Design & Statistical Analysis",
      "Data Acquisition & Signal Processing",
      "MATLAB / Simulink for Aerospace Modeling",
      "ANSYS Mechanical & Fluent for FEA/CFD",
      "Technical Writing & Academic Publishing (LaTeX)"
    ]
  };

  return (
    <div className={styles.container}>

      {/* ── EDUCATION TIMELINE ── */}
      <section className={styles.timeline}>
        {eduItems.length === 0 ? (
          <p>No education history available.</p>
        ) : (
          eduItems.map((edu, idx) => {
            const SchoolLogo = logoMap[edu.school];
            const isAAUB = edu.school?.includes("Aviation and Aerospace");

            return (
              <article
                className={styles.card}
                key={idx}
                
              >
                {/* Degree Header */}
                <div className={styles.headerInfo}>
                  {SchoolLogo ? (
                    <img
                      src={SchoolLogo}
                      alt={`${edu.school} logo`}
                      className={styles.logo}
                      loading="lazy"
                    />
                  ) : (
                    <div
                      className={styles.logo}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.8rem',
                        background: 'var(--muted)',
                      }}
                    >
                      🎓
                    </div>
                  )}
                  <div className={styles.titleWrapper}>
                    <h3 className={styles.degreeTitle}>{edu.degree}</h3>
                    <p className={styles.institution}>{edu.school}</p>
                  </div>
                </div>

                {/* Metadata */}
                <div className={styles.metaGrid}>
                  {edu.duration && (
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Duration</span>
                      <span className={styles.metaValue}>{edu.duration}</span>
                    </div>
                  )}
                  {edu.gpa && edu.gpa.trim() !== "" && (
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Grade / Score</span>
                      <span className={`${styles.metaValue} ${styles.gpaBadge}`}>
                        {edu.gpa}
                      </span>
                    </div>
                  )}
                </div>

                {/* Description */}
                {edu.description && (
                  <p className={styles.description}>{edu.description}</p>
                )}

                {/* Thesis Section — Enhanced for PhD */}
                {edu.thesis && (
                  <div className={styles.thesisSection}>
                    <button
                      className={styles.thesisToggle}
                      onClick={() => setExpandedThesis(expandedThesis === idx ? null : idx)}
                    >
                      <div className={styles.thesisLeft}>
                        <span style={{ fontSize: '1.1rem' }}>
                          {expandedThesis === idx ? '▼' : '▶'}
                        </span>
                        <span style={{ fontWeight: 700 }}>
                          Thesis Details
                        </span>
                      </div>
                      <span
                        className={styles.thesisStatus}
                        style={{
                          background: 'rgba(250, 204, 21, 0.15)',
                          color: '#d97706',
                          border: '1px solid rgba(250, 204, 21, 0.3)',
                        }}
                      >
                        🏆 {edu.thesis.status || 'Best Thesis Award 2025'}
                      </span>
                    </button>

                    {expandedThesis === idx && (
                      <div className={styles.thesisDetails}>
                        <h4 className={styles.thesisTitle}>{edu.thesis.title}</h4>

                        <div className={styles.thesisSupervisor}>
                          <strong>Supervisor:</strong> {edu.thesis.supervisor}{' '}
                          <br />
                          <span style={{ fontSize: '0.85rem' }}>
                            {edu.thesis.supervisorTitle}
                          </span>
                        </div>

                        {/* Research Significance — NEW */}

                        {edu.thesis.objectives && (
                          <div>
                            <h5 className={styles.listTitle}>Research Objectives</h5>
                            <ul className={styles.bulletList}>
                              {edu.thesis.objectives.map((obj, j) => (
                                <li key={j}>{obj}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {edu.thesis.keyAchievements && (
                          <div>
                            <h5 className={styles.listTitle}>Key Achievements</h5>
                            <ul className={styles.bulletList}>
                              {edu.thesis.keyAchievements.map((ach, j) => (
                                <li key={j}>{ach}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {edu.thesis.technologies && (
                          <div>
                            <h5 className={styles.listTitle}>Technologies & Methods</h5>
                            <div className={styles.techTags}>
                              {edu.thesis.technologies.map((tech, j) => (
                                <span key={j} className={styles.techTag}>
                                  {tech}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Publication Link — NEW */}
                        <div style={{ marginTop: '1.2rem' }}>
                          <button
                            onClick={() => navigate('/research/e2e-pdm-framework')}
                            className={styles.thesisToggle}
                            style={{
                              background: 'var(--accent)',
                              color: '#fff',
                              border: 'none',
                              justifyContent: 'center',
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            View Full Research Publication →
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </article>
            );
          })
        )}
      </section>

      {/* ── RESEARCH PREPARATION SECTION — NEW ── */}
      <section style={{ marginTop: '4rem', marginBottom: '2rem' }}>
        {/* Three Competency Cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem',
          }}
        >
          {/* Card 1: Core Coursework */}
          <div
            style={{
              background: 'var(--card)',
              border: '1px solid var(--border)',
              borderRadius: '12px',
              padding: '1.5rem',
              
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.06)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            
            <h3
              style={{
                margin: '0 0 0.5rem 0',
                fontSize: '1.1rem',
                fontWeight: 700,
                color: 'var(--fg)',
              }}
            >
              Core Aerospace Coursework
            </h3>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {researchPrep.coreCourses.map((c, i) => (
                <span
                  key={i}
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    padding: '0.25rem 0.6rem',
                    background: 'var(--muted)',
                    color: 'var(--text-secondary)',
                    borderRadius: '4px',
                    border: '1px solid var(--border)',
                  }}
                >
                  {c}
                </span>
              ))}
            </div>
          </div>

          {/* Card 2: Research Methods & Tools */}
          <div
            style={{
              background: 'var(--card)',
              border: '1px solid var(--border)',
              borderRadius: '12px',
              padding: '1.5rem',
              
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.06)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            
            <h3
              style={{
                margin: '0 0 0.5rem 0',
                fontSize: '1.1rem',
                fontWeight: 700,
                color: 'var(--fg)',
              }}
            >
              Research Methods & Computational Tools
            </h3>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {researchPrep.researchMethods.map((c, i) => (
                <span
                  key={i}
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    padding: '0.25rem 0.6rem',
                    background: 'rgba(4, 120, 87, 0.08)',
                    color: '#047857',
                    borderRadius: '4px',
                    border: '1px solid rgba(4, 120, 87, 0.2)',
                  }}
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
          
        </div>
      </section>

{/* ── MEMORIES GALLERY ── */}
      {memories.length > 0 && (
        <section className={styles.gallerySection}>
          <div className={styles.galleryHeader}>
            <h2 className={styles.galleryTitle}>Academic Journey</h2>
            
          </div>

          <div className={styles.galleryGrid}>
            {memories.map((item, idx) => (
              <figure key={idx} className={styles.galleryFigure}>
                <img
                  src={item.image}
                  alt={item.caption}
                  className={styles.galleryImage}
                  loading="lazy"
                />
                <figcaption className={styles.galleryCaption}>
                  {item.caption}
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}